"use client";

import clsx from "clsx";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {usePreviewFetch} from "@/hooks/useFilePreview";
import {parseTextFromResponse} from "@/utils/filePreview";
import {buildHtmlPreviewDocument, htmlPreviewSandbox} from "@/utils/htmlPreview";

const MAX_HTML_BYTES = 5 * 1024 * 1024;

const InlineHtmlPreview: React.FC<{
   url: string;
   fileName: string;
   peek?: boolean;
}> = ({url, fileName, peek = false}) => {
   const {t} = useTranslation("ask");
   const {data, error, loading} = usePreviewFetch(url, (res) =>
      parseTextFromResponse(res, MAX_HTML_BYTES)
   );

   const className = clsx("w-full", peek ? "h-56 overflow-hidden" : "h-full min-h-64");
   const previewDocument = useMemo(
      () => data && !data.truncated ? buildHtmlPreviewDocument(data.content, fileName, peek) : "",
      [data, fileName, peek]
   );

   // Never execute a truncated document: a missing script tail can silently
   // leave an otherwise convincing dashboard with incomplete data.
   if (error || data?.truncated || (data && !data.content.trim())) {
      return (
         <div className={clsx(className, "flex items-center justify-center text-sm text-500")}>
            {t(
               data?.truncated
                  ? "preview.htmlTooLarge"
                  : data && !data.content.trim()
                    ? "preview.emptyFile"
                    : "preview.failedToLoad"
            )}
         </div>
      );
   }

   if (loading || !data) {
      return (
         <div className={clsx(className, "flex items-center justify-center")} aria-busy="true">
            <div className="w-5 h-5 border-2 border-300 border-t-primary rounded-full animate-spin" />
         </div>
      );
   }

   return (
      <div className={className}>
         {/* Fetch through the authenticated preview path, then use srcDoc so
             attachment response headers cannot turn the preview into a download.
             Downloads are enabled only in the interactive panel so exports work
             without allowing passive chat thumbnails to initiate downloads.
             Never add allow-same-origin: generated scripts must remain isolated
             from the parent DOM, cookies, and storage. */}
         <iframe
            title={fileName}
            srcDoc={previewDocument}
            sandbox={htmlPreviewSandbox(peek)}
            referrerPolicy="no-referrer"
            tabIndex={peek ? -1 : undefined}
            aria-hidden={peek || undefined}
            className={clsx(
               "block border-0 bg-white",
               peek
                  ? "w-[200%] h-[448px] origin-top-left scale-50 pointer-events-none"
                  : "w-full h-full min-h-64"
            )}
         />
      </div>
   );
};

export default InlineHtmlPreview;
